import { Fragment } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { zerlegeText } from '@mcikm-vote/shared';
import type { Textblock, Textteil } from '@mcikm-vote/shared';

/**
 * Anzeige eines formatierten Abstimmungstexts (§12).
 *
 * **Hier entsteht kein HTML-String.** Der Text wird in einen typisierten Baum zerlegt und dieser
 * als React-Elemente ausgegeben; `dangerouslySetInnerHTML` kommt nicht vor. Alles, was §12
 * verbietet — Bilder, Tabellen, eingebettete Inhalte, CSS, JavaScript —, hat im Baum keine
 * Entsprechung und kann deshalb gar nicht entstehen.
 *
 * Dieselbe Komponente zeigt den Text in der Teilnehmeransicht und in der Vorschau des
 * Verwaltungsbereichs. Nur so kann die Vorschau nicht von dem abweichen, was die Teilnehmer
 * tatsächlich sehen.
 */
export function FormatierterText({ text }: { text: string }): ReactElement {
  const bloecke = zerlegeText(text);

  return (
    <div className="flex flex-col gap-3">
      {bloecke.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: Textblock }): ReactElement {
  if (block.art === 'absatz') {
    return <p>{teile(block.teile)}</p>;
  }

  const klassen = 'flex flex-col gap-1 pl-5';

  return block.nummeriert ? (
    <ol className={`${klassen} list-decimal`}>
      {block.punkte.map((punkt, index) => (
        <li key={index}>{teile(punkt)}</li>
      ))}
    </ol>
  ) : (
    <ul className={`${klassen} list-disc`}>
      {block.punkte.map((punkt, index) => (
        <li key={index}>{teile(punkt)}</li>
      ))}
    </ul>
  );
}

function teile(liste: readonly Textteil[]): ReactNode {
  return liste.map((teil, index) => <Teil key={index} teil={teil} />);
}

function Teil({ teil }: { teil: Textteil }): ReactElement {
  switch (teil.art) {
    case 'text':
      return <Fragment>{teil.text}</Fragment>;

    case 'fett':
      return <strong className="font-semibold">{teile(teil.teile)}</strong>;

    case 'kursiv':
      return <em className="italic">{teile(teil.teile)}</em>;

    case 'link':
      // Das Ziel hat der Zerleger bereits auf http und https eingegrenzt (§12).
      // `noopener` und `noreferrer` verhindern, dass die geöffnete Seite auf diese zugreift.
      return (
        <a
          href={teil.ziel}
          target="_blank"
          rel="noopener noreferrer"
          className="text-akzent underline"
        >
          {teile(teil.teile)}
        </a>
      );
  }
}
